import { useState, useEffect, useRef } from 'react';
import { MapPin, X, Check, Search, Navigation2, Plus, Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../../lib/apiConfig';

interface MapAddressSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAddress: (address: AddressDetails) => void;
  initialLocation?: { lat: number; lng: number };
  existingLabels?: string[];
  autoSelectInitial?: boolean;
}

interface AddressDetails {
  formattedAddress: string;
  street?: string;
  area?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  lat: number;
  lng: number;
  landmark?: string;
  houseNumber?: string;
  floor?: string;
  addressType?: 'Home' | 'Work' | 'Other';
  addressLabel?: string;
}

export function MapAddressSelector({
  isOpen,
  onClose,
  onSelectAddress,
  initialLocation,
  existingLabels = [],
  autoSelectInitial = true,
}: MapAddressSelectorProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<any>(null);
  const [marker, setMarker] = useState<any>(null);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(
    initialLocation || null
  );
  const [addressDetails, setAddressDetails] = useState<AddressDetails | null>(null);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showDetailsForm, setShowDetailsForm] = useState(false);
  const [manualAddress, setManualAddress] = useState({
    houseNumber: '',
    floor: '',
    landmark: '',
    addressType: 'Home' as 'Home' | 'Work' | 'Other',
  });
  const [deliveryForm, setDeliveryForm] = useState({
    useAccountDetails: false,
    receiverName: '',
    receiverPhone: '',
    buildingFloor: '',
    street: '',
    areaLocality: '',
    deliveryInstructions: '',
    addressLabel: 'Home',
  });

  // Initialize map on mount
  useEffect(() => {
    if (!isOpen || !mapRef.current || map) return;

    // Dynamically load Leaflet CSS and JS
    const loadLeaflet = async () => {
      // Load CSS
      if (!document.querySelector('link[href*="leaflet.css"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }

      // Load JS
      if (!(window as any).L) {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.async = true;
        document.head.appendChild(script);

        await new Promise((resolve) => {
          script.onload = resolve;
        });
      }

      initializeMap();
    };

    loadLeaflet();
  }, [isOpen, mapRef.current]);

  useEffect(() => {
    if (!isOpen || !initialLocation) return;
    setSelectedLocation(initialLocation);
    if (map && marker) {
      map.setView([initialLocation.lat, initialLocation.lng], 16);
      marker.setLatLng([initialLocation.lat, initialLocation.lng]);
      if (autoSelectInitial) {
        handleLocationSelect(initialLocation.lat, initialLocation.lng);
      }
    }
  }, [isOpen, initialLocation?.lat, initialLocation?.lng, map, marker, autoSelectInitial]);

  useEffect(() => {
    if (!isOpen) return;
    if (!autoSelectInitial) {
      setAddressDetails(null);
      setShowDetailsForm(false);
      setSearchQuery('');
      setSearchResults([]);
      setIsLoadingAddress(false);
    }
  }, [isOpen, autoSelectInitial]);

  const initializeMap = () => {
    const L = (window as any).L;
    if (!L || !mapRef.current) return;

    const defaultCenter = selectedLocation || { lat: 12.9716, lng: 77.5946 }; // Bangalore

    const newMap = L.map(mapRef.current, { attributionControl: false }).setView([defaultCenter.lat, defaultCenter.lng], 13);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '',
      maxZoom: 19,
    }).addTo(newMap);

    // Add custom marker
    const customIcon = L.divIcon({
      className: 'custom-marker',
      html: `
        <div style="position: relative;">
          <div style="width: 40px; height: 40px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 50% 50% 50% 0; transform: rotate(-45deg); box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4); border: 3px solid white;"></div>
          <div style="position: absolute; top: 8px; left: 8px; width: 24px; height: 24px; background: white; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#667eea" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
          </div>
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
    });

    const newMarker = L.marker([defaultCenter.lat, defaultCenter.lng], {
      icon: customIcon,
      draggable: true,
    }).addTo(newMap);

    // Handle marker drag
    newMarker.on('dragend', (e: any) => {
      const position = e.target.getLatLng();
      handleLocationSelect(position.lat, position.lng);
    });

    // Handle map click
    newMap.on('click', (e: any) => {
      const { lat, lng } = e.latlng;
      newMarker.setLatLng([lat, lng]);
      handleLocationSelect(lat, lng);
    });

    setMap(newMap);
    setMarker(newMarker);

    // Load initial address if location provided and auto-select enabled
    if (selectedLocation && autoSelectInitial) {
      handleLocationSelect(selectedLocation.lat, selectedLocation.lng);
    }
  };

  const handleLocationSelect = async (lat: number, lng: number) => {
    setSelectedLocation({ lat, lng });
    setIsLoadingAddress(true);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
      );
      const data = await response.json();

      const address: AddressDetails = {
        formattedAddress: data.display_name,
        street: data.address?.road || '',
        area: data.address?.suburb || data.address?.neighbourhood || '',
        city: data.address?.city || data.address?.town || data.address?.village || '',
        state: data.address?.state || '',
        postalCode: data.address?.postcode || '',
        country: data.address?.country || '',
        lat,
        lng,
      };

      setAddressDetails(address);
    } catch (error) {
      console.error('Error fetching address:', error);
      setAddressDetails({
        formattedAddress: `Location: ${lat.toFixed(4)}, ${lng.toFixed(4)}`,
        lat,
        lng,
      });
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const handleSearchLocation = async (queryOverride?: string, silent = false) => {
    const query = (queryOverride ?? searchQuery).trim();
    if (!query) return;

    setIsSearching(true);
    if (query.length < 3) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}&limit=5&addressdetails=1&countrycodes=in`,
        {
          headers: {
            'Accept': 'application/json',
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Search failed');
      }
      
      const data = await response.json();
      console.log('Search results:', data); // Debug log
      setSearchResults(data);
      
      // If we have results, automatically select the first one
      if (!data.length && !silent) {
        alert('No results found. Please try a different search term.');
      }
    } catch (error) {
      console.error('Error searching location:', error);
      if (!silent) {
        alert('Failed to search location. Please check your internet connection and try again.');
      }
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const query = searchQuery.trim();
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(() => {
      handleSearchLocation(query, true);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const handleSearchResultClick = (result: any) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);

    if (map && marker) {
      map.setView([lat, lng], 16);
      marker.setLatLng([lat, lng]);
      handleLocationSelect(lat, lng);
    }

    setSearchResults([]);
    setSearchQuery('');
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        if (map && marker) {
          map.setView([latitude, longitude], 16);
          marker.setLatLng([latitude, longitude]);
          handleLocationSelect(latitude, longitude);
        }
      },
      (error) => {
        alert('Unable to get your location. Please try again.');
      }
    );
  };

  const handleConfirmAddress = () => {
    if (!addressDetails) return;
    setDeliveryForm((prev) => ({
      ...prev,
      street: addressDetails.street || '',
      areaLocality: addressDetails.area || addressDetails.city || addressDetails.formattedAddress,
      addressLabel: manualAddress.addressType,
    }));
    setShowDetailsForm(true);
  };

  const handleSaveAddress = async () => {
    if (!addressDetails) return;
    // Label should match currently selected save type in this UI (Home/Work/Other).
    const finalLabel = String(manualAddress.addressType || '').trim();
    if (!finalLabel) {
      alert('Please enter Save address as label.');
      return;
    }

    const finalAddress: AddressDetails = {
      ...addressDetails,
      houseNumber: manualAddress.houseNumber || undefined,
      floor: manualAddress.floor,
      landmark: manualAddress.landmark || deliveryForm.deliveryInstructions || undefined,
      addressType: manualAddress.addressType,
      addressLabel: finalLabel,
    };

    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (!token) {
      alert('Please login again to save address.');
      return;
    }

    const saveEndpoint = `${API_BASE_URL}/users/saved-locations`;

    const payload = {
      label: finalLabel,
      addressType: manualAddress.addressType,
      receiverName: null,
      receiverPhone: '0000000000',
      buildingFloor: `${manualAddress.houseNumber || ''}${manualAddress.floor ? `, ${manualAddress.floor}` : ''}`.trim() || 'N/A',
      street: addressDetails.street || null,
      areaLocality: addressDetails.area || addressDetails.city || addressDetails.formattedAddress,
      fullAddress: addressDetails.formattedAddress,
      deliveryInstructions: manualAddress.landmark || deliveryForm.deliveryInstructions || null,
      latitude: addressDetails.lat,
      longitude: addressDetails.lng,
      isDefault: false,
    };

    let response: Response;
    try {
      response = await fetch(saveEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
    } catch {
      alert('Failed to save address. Please check your internet connection and try again.');
      return;
    }

    if (!response.ok) {
      const errorJson = await response.json().catch(() => ({}));
      const message = String(errorJson?.message || 'Failed to save address.');
      if (message.toLowerCase().includes('already exists')) {
        alert('This address label already exists. Please use a different label.');
      } else {
        alert(message);
      }
      return;
    }

    onSelectAddress(finalAddress);
    onClose();
  };

  const handleChangeLocation = () => {
    setShowDetailsForm(false);
    if (selectedLocation && map && marker) {
      map.setView([selectedLocation.lat, selectedLocation.lng], 16);
      marker.setLatLng([selectedLocation.lat, selectedLocation.lng]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center">
      <div className="bg-white w-full md:max-w-4xl h-[90vh] md:h-[85vh] md:rounded-2xl rounded-t-3xl flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 flex items-center justify-between shadow-lg">
          <div>
            <h2 className="text-xl font-bold text-white">Select Delivery Location</h2>
            <p className="text-blue-100 text-sm mt-1">Click on map or search to set your location</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-all"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 bg-gradient-to-b from-gray-50 to-white border-b border-gray-200 relative z-[1200]">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (!e.target.value.trim()) setSearchResults([]);
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchLocation()}
                placeholder="Search for area, street name..."
                className="w-full px-4 py-3 pl-12 rounded-xl border-2 border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none transition-all"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              
              {/* Search Results Dropdown */}
              {searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-blue-300 rounded-xl shadow-2xl max-h-64 overflow-y-auto z-[2000]">
                  <div className="p-2">
                    <p className="text-xs text-gray-500 font-semibold px-2 py-1">Search Results ({searchResults.length})</p>
                  </div>
                  {searchResults.map((result, index) => (
                    <button
                      key={index}
                      onClick={() => handleSearchResultClick(result)}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0 flex items-start gap-3 group"
                    >
                      <div className="w-10 h-10 bg-blue-100 group-hover:bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0 transition-colors">
                        <MapPin className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 mb-0.5 line-clamp-2">{result.display_name}</p>
                        {result.type && (
                          <p className="text-xs text-gray-500 capitalize">
                            <span className="inline-block px-2 py-0.5 bg-gray-100 rounded-full">{result.type}</span>
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => {
                void handleSearchLocation();
              }}
              disabled={isSearching || !searchQuery.trim()}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSearching ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="hidden sm:inline">Searching...</span>
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  <span className="hidden sm:inline">Search</span>
                </>
              )}
            </button>
            <button
              onClick={handleUseCurrentLocation}
              className="px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:shadow-lg transition-all"
              title="Use current location"
            >
              <Navigation2 className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Map Container */}
        <div className="flex-1 relative">
          <div ref={mapRef} className="w-full h-full" />
          
          {/* Loading Overlay */}
          {isLoadingAddress && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-[1000]">
              <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
              <span className="text-sm font-medium text-gray-700">Getting address...</span>
            </div>
          )}
        </div>

        {/* Address Details Panel */}
        {addressDetails && (
          <div className="bg-white border-t-2 border-gray-200 p-6 max-h-[45vh] overflow-y-auto">
            <div className="space-y-4">
              {!showDetailsForm ? (
                <>
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-xl border-2 border-blue-200">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 mb-1">Selected Location</p>
                        <p className="text-gray-900 font-semibold leading-relaxed">
                          {addressDetails.formattedAddress}
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleConfirmAddress}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-6 rounded-xl font-bold text-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Check className="w-6 h-6" />
                    <span>Confirm Location & Proceed</span>
                  </button>
                </>
              ) : (
                <div className="space-y-5">
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-xl border-2 border-blue-200">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-600 mb-1">Selected Location</p>
                          <p className="text-gray-900 font-semibold leading-relaxed">
                            {addressDetails.formattedAddress}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleChangeLocation}
                        className="px-4 py-2 border border-orange-300 text-orange-600 bg-orange-50 rounded-lg font-semibold"
                      >
                        Change
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3">Complete Address Details</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">House/Flat No.</label>
                        <input
                          type="text"
                          value={manualAddress.houseNumber}
                          onChange={(e) => setManualAddress({ ...manualAddress, houseNumber: e.target.value })}
                          placeholder="e.g., 123"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Floor (Optional)</label>
                        <input
                          type="text"
                          value={manualAddress.floor}
                          onChange={(e) => setManualAddress({ ...manualAddress, floor: e.target.value })}
                          placeholder="e.g., 2nd Floor"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nearby Landmark (Optional)</label>
                      <input
                        type="text"
                        value={manualAddress.landmark}
                        onChange={(e) => setManualAddress({ ...manualAddress, landmark: e.target.value })}
                        placeholder="e.g., Near City Mall"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Save As</label>
                      <div className="flex gap-2">
                        {(['Home', 'Work', 'Other'] as const).map((type) => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => setManualAddress({ ...manualAddress, addressType: type })}
                            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                              manualAddress.addressType === type
                                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleSaveAddress}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-6 rounded-xl font-bold text-lg"
                  >
                    Save Address
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
